import React, { memo } from 'react';

import { BASE_FONT_SIZE } from '@front/common/src/constants';
import { ButtonSize, ButtonType } from '@front/common/src/types/components';

import { translate } from '@front/i18n';

import Icon from '../icon/icon';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { WithClassNameProps } from '../types';

import { getColors, StyledButton, StyledIconWrapper, StyledLoadingWrapper, BUTTON_TEXT_BY_SIZE } from './common';

const t = translate('components.button');

export type LinkButtonProps = WithClassNameProps & {
  href: string;
  title?: string;
  icon?: string;
  openInNewTab?: boolean;
  onClick?: () => void;
  children: string | number;
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: ButtonSize;
  hasFullWidth?: boolean;
  isRound?: boolean;
  type?: ButtonType;
};

const LinkButton = ({
  href,
  title,
  children,
  hasFullWidth = false,
  size = 'md',
  type = 'primary',
  isDisabled = false,
  isLoading = false,
  isRound = true,
  icon,
  openInNewTab = false,
  className,
  onClick,
}: LinkButtonProps) => {
  const ButtonText = BUTTON_TEXT_BY_SIZE[size];

  const { color, backgroundColor, borderColor, iconColor, loadingSpinnerColor } = getColors(type, isLoading);

  const shouldBeDisabled = isDisabled || isLoading;

  return (
    <StyledButton
      as='a'
      onClick={onClick}
      href={shouldBeDisabled ? undefined : href}
      title={title}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener' : undefined}
      className={className}
      $isRound={isRound}
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $type={type}
      $size={size}
      $hasFullWidth={hasFullWidth}
      $isDisabled={shouldBeDisabled}
    >
      {!!icon && (
        <StyledIconWrapper>
          <Icon name={icon} color={iconColor} size={BASE_FONT_SIZE} />
        </StyledIconWrapper>
      )}
      {isLoading && (
        <StyledLoadingWrapper>
          <LoadingSpinner color={loadingSpinnerColor} title={t('.loading')} />
        </StyledLoadingWrapper>
      )}
      <ButtonText color={color} centered>
        {children}
      </ButtonText>
    </StyledButton>
  );
};

export default memo(LinkButton);
